import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { SnackbarService } from '@fitscovery/ui/snackbar';
import { ApplicationChecker } from '@fitscovery/common/classes/application-checker';
import { WINDOW } from '@ng-web-apis/common';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import firebase from 'firebase/app';
import "firebase/auth";
import { produce } from 'immer';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { ConfirmEmailVerification, GetToken, LoginWithAccessToken, LoginWithCustomToken, LoginWithCustomTokenRedirect, LoginWithEmailAndPassword, LoginWithFacebook, LoginWithFirebaseState, LoginWithGoogle, Logout, ResetAuthToDefault, SendEmailVerification, CreateCustomToken, SetLoggedInUser, SetToken, SignOutRevokeUserToken, SignUp, UpdateUser } from '../actions';
import { AuthProviders } from '../enums';
import { AuthStateErrorResponse, AuthStateModel, ConfirmEmailVerificationConfig } from '../interfaces';
import { CreateCustomTokenResponse, User, UserResponse } from '../models';
import { ApiAuthService } from '../services/api-auth.service';

import { FitscoveryError, FitscoveryGenericErrorCode } from '@fitscovery/common/types';

export function parseAndThrowError(error: unknown) {
  if (error instanceof FitscoveryError) {
    throw error;
  } else if (error instanceof Error) {
    throw new FitscoveryError({
      message: error.message,
      code: FitscoveryGenericErrorCode.UnknownError,
      title: error.name,
      stack: error.stack,
      data: error
    });
  } else {
    throw error;
  }
}


export const authStateDefaults: AuthStateModel = {
  loaded: false,
  token: null,
  user: null,
  accessToken: null,
  confirmEmailVerification: {
    isSent: false,
    hasError: false,
    errorMessage: ''
  }
};

@State<AuthStateModel>({
  name: 'auth',
  defaults: authStateDefaults
})
@Injectable()
export class AuthState extends ApplicationChecker implements NgxsOnInit {

  @Selector()
  static loaded(state: AuthStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static token(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static accessToken(state: AuthStateModel): string | null {
    return state.accessToken;
  }

  @Selector()
  static user(state: AuthStateModel): User | null {
    return state.user;
  }

  @Selector()
  static error(state: AuthStateModel): AuthStateErrorResponse | undefined {
    return state.error;
  }

  @Selector()
  static confirmEmailVerification(state: AuthStateModel): ConfirmEmailVerificationConfig {
    return state.confirmEmailVerification;
  }

  constructor(
    @Inject(WINDOW) public override window: any,
    private router: Router,
    private store: Store,
    private authClient: AngularFireAuth,
    private authService: ApiAuthService,
    private snackbarService: SnackbarService
  ) {
    super(window);
  }

  ngxsOnInit(ctx: StateContext<AuthStateModel>): void {

    this.authClient.authState.pipe(
      tap((user: any) => {

        const token = ctx.getState().token;
        const hasToken = token!?.length > 0;
        const hasNoSession = !user; 
        const urlParams = new URLSearchParams(this.window.location.search);
        const hasAutoLogin =  urlParams.get('auto-login') === 'true';

        // logout user when signed out in other browsers/devices
        if (hasToken && hasNoSession && !hasAutoLogin) {
          const message = 'Session expired! Please sign in again.';
          this.store.dispatch(new Logout(message));
        }
      })
    ).subscribe();

    this.authClient.user.pipe(
      filter(() => this.isPartnerWeb),         // Target application: Partner.NG
      filter(() => !ctx.getState().loaded),
      filter((user: any) => !!user),
      map((user: any) => this.setTokenAndUser(ctx, user as User))
    ).subscribe();

  }

  @Action(GetToken)
  async getToken(ctx: StateContext<AuthStateModel>, action: GetToken): Promise<void> {
    try {
      const token: string = await (await this.authClient.user.toPromise())?.getIdToken()!;
      ctx.dispatch(new SetToken(token));
    } catch (error) {
      // Parse the error and transform it into the company's custom error object with: error code, message, and what users wll do if it's too technical
      parseAndThrowError(error);
    }
  }

  @Action(SignUp)
  signUp(ctx: StateContext<AuthStateModel>, action: SignUp){

    const email = action.user.email!;
    const password = action.user.password;

    this.authService.signUp(action.user).pipe(
      tap(() => ctx.dispatch(new LoginWithEmailAndPassword(email, password)))
    ).subscribe();
  }

  @Action(UpdateUser)
  updateUser(ctx: StateContext<AuthStateModel>, action: UpdateUser): Observable<UserResponse> {
    return this.authService.updateUser(action.user).pipe(
      tap((response: any) => ctx.patchState({ user: { ...ctx.getState().user, ...response.data } })),
      catchError((response: HttpErrorResponse) => {
        this.snackbarService.openSnack({ message: response.error.errors[0] });
        return throwError(response)
      })
    );
  }

  @Action(LoginWithEmailAndPassword)
  async loginWithEmailAndPassword(ctx: StateContext<AuthStateModel>, action: LoginWithEmailAndPassword): Promise<void> {
    try {
      const state = ctx.getState();

      if (state.loaded) {
        throw new FitscoveryError({
          message: 'User has already logged in according to the state slice.',
          code: FitscoveryGenericErrorCode.ClientSideError
        });
      }

      await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const userResult: firebase.auth.UserCredential = await this.authClient.signInWithEmailAndPassword(action.email, action.password);
      await this.setTokenAndUser(ctx, userResult.user as User);
    } catch (error) {
      ctx.patchState({ error });
      // Parse the error and transform it into the company's custom error object with: error code, message, and what users wll do if it's too technical
      parseAndThrowError(error);
    }
  }

  @Action(LoginWithFacebook)
  async loginWithFacebook(ctx: StateContext<AuthStateModel>): Promise<void> {
    try {
      await this.loginWithProvider(AuthProviders.Facebook, ctx);
    } catch (error) {
      // Parse the error and transform it into the company's custom error object with: error code, message, and what users wll do if it's too technical
      parseAndThrowError(error);
    }
  }

  @Action(LoginWithGoogle)
  async loginWithGoogle(ctx: StateContext<AuthStateModel>): Promise<void> {
    try {
      await this.loginWithProvider(AuthProviders.Google, ctx);
    } catch (error) {
      // Parse the error and transform it into the company's custom error object with: error code, message, and what users wll do if it's too technical
      parseAndThrowError(error);
    }
  }
  
  @Action(LoginWithFirebaseState)
  loginWithFirebaseState(ctx: StateContext<AuthStateModel>) {
    this.authClient.authState.pipe(
      tap((user: any) => this.setTokenAndUser(ctx, user as User)),
      take(1)
    ).subscribe();
  }

  @Action(LoginWithAccessToken)
  createToken(ctx: StateContext<AuthStateModel>, action: LoginWithAccessToken) {
    return this.authClient.authState.pipe(
      switchMap((state) => state?.getIdToken() ? from(state.getIdToken()) as any : of(false)),
      filter((e: any) => !!e),
      switchMap((token: string) => this.authService.createCustomTokenManual(token)),
      tap(async (response: CreateCustomTokenResponse) => {
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
        const userResult = await this.authClient.signInWithCustomToken(response.accessToken);
        await this.setTokenAndUser(ctx, userResult.user as User);
      }),
      take(1)
    );
  }

  @Action(SignOutRevokeUserToken)
  signOutRevokeUserToken(ctx: StateContext<AuthStateModel>) {
    return this.authService.signOutRevokeUserToken().pipe(
      tap(() => ctx.dispatch(new Logout))
    );
  }

  @Action(Logout)
  async logout(ctx: StateContext<AuthStateModel>, action: Logout) {
    try {
      await this.authClient.signOut();

      !this.isPartnerWeb ? this.applicationSignOut(action.message) : null;

    } catch (error) {
      parseAndThrowError(error);
    }
  }

  @Action(SetLoggedInUser)
  setLoggedInUser(ctx: StateContext<AuthStateModel>, action: SetLoggedInUser): void {
    ctx.patchState(<AuthStateModel>{
      user: {
        id: action.user.id,
        uid: action.user.uid,
        firebaseId: action.user.firebaseId,
        email: action.user.email,
        displayName: action.user.displayName,
        firstName: action.user.firstName,
        lastName: action.user.lastName,
        gender: action.user.gender,
        birthdate: action.user.birthdate,
        phoneNumber: action.user.phoneNumber,
        imageUrl: action.user.imageUrl
      }
    });
  }

  @Action(SetToken)
  setToken(ctx: StateContext<AuthStateModel>, action: SetToken): void {
    ctx.patchState(<AuthStateModel>{
      token: action.token
    });
  }

  @Action(CreateCustomToken)
  createAccessToken(ctx: StateContext<AuthStateModel>) {
    return this.authService.createCustomToken().pipe(
      map((response: CreateCustomTokenResponse) => response.accessToken),
      tap((accessToken: string) => ctx.patchState(<AuthStateModel>{ accessToken }))
    );
  }

  @Action(LoginWithCustomToken)
  async loginWithCustomToken(ctx: StateContext<AuthStateModel>, action: LoginWithCustomToken): Promise<void> {
    try {
      await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const userResult: firebase.auth.UserCredential = await this.authClient.signInWithCustomToken(action.token);

      await this.setTokenAndUser(ctx, userResult.user as User);
    } catch (error) {
      parseAndThrowError(error);
    }
  }

  @Action(ResetAuthToDefault)
  resetAuthToDefault(ctx: StateContext<AuthStateModel>, action: ResetAuthToDefault) {
    ctx.setState(authStateDefaults);
  }

  @Action(LoginWithCustomTokenRedirect)
  loginWithCustomTokenRedirect(ctx: StateContext<AuthStateModel>, action: LoginWithCustomTokenRedirect) {
    of([]).pipe(
      switchMap(() => this.store.dispatch(new Logout)),
      delay(1000),
      switchMap(() => this.store.dispatch(new LoginWithCustomToken(action.customLoginToken))),
      switchMap(() => from(this.router.navigate([ 'dashboard' ], {
        queryParamsHandling: 'merge',
        queryParams: { p: action.autoLoginSubdomain, highlight: true }
      }))),
    ).subscribe();
  }

  @Action(ConfirmEmailVerification)
  confirmEmailVerification(ctx: StateContext<AuthStateModel>, action: ConfirmEmailVerification): void {
    const state = ctx.getState();
    this.authService.confirmEmailVerification(action.verificationCode).pipe(
      tap(() => {
        const value = produce(state, draft => {
          draft.confirmEmailVerification.isSent = true;
          draft.confirmEmailVerification.hasError = false;
          draft.confirmEmailVerification.errorMessage = '';
        });
        ctx.setState(value);
      }),
      catchError((response: HttpErrorResponse) => {
        const value = produce(state, draft => {
          draft.confirmEmailVerification.isSent = false;
          draft.confirmEmailVerification.hasError = true;
          draft.confirmEmailVerification.errorMessage = response.error.errors[0];
        });
        ctx.setState(value);
        return EMPTY;
      })
    ).subscribe();
  }

  @Action(SendEmailVerification)
  sendEmailVerification(ctx: StateContext<AuthStateModel>, action: SendEmailVerification): void {
    this.authService.sendEmailVerification().pipe(
      catchError((response: HttpErrorResponse) => {
        const message = 'Sorry. An unexpected error occurred. Kindly try again or if error persists, contact francis@fitscovery.com';
        this.store.dispatch(new Logout(message));
        return EMPTY;
      })
    ).subscribe(() => {
      this.snackbarService.openSnack({ message: 'Email verification sent!' });
      ctx.setState(
        produce( ctx.getState(), (draft: any) => { draft.user['sent'] = true } )
      )
    });
  }

  private async loginWithProvider(providerId: AuthProviders | number, ctx: StateContext<AuthStateModel>, redirectUrl?: string): Promise<void> {
    let provider: any = null;

    switch (providerId) {
      case AuthProviders.Github:
        provider = new firebase.auth.GithubAuthProvider();
        break;
      case AuthProviders.Twitter:
        provider = new firebase.auth.TwitterAuthProvider();
        break;
      case AuthProviders.Facebook:
        provider = new firebase.auth.FacebookAuthProvider();
        break;
      case AuthProviders.Google:
        provider = new firebase.auth.GoogleAuthProvider();
        break;
    }
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    try {
      this.window.location.hash = "redirecting";
      await firebase.auth().signInWithRedirect(provider);
    } catch (error: any) {
      // TODO: [Francis] Log in third party logger
      // console.error('ERROR @ AuthService#signIn() :', errorCode + '|' + errorMessage);

      // TODO: [Francis] Refactor this to have auth error checking for the FirebaseErrorCodes
      if (error.code === 'authService/account-exists-with-different-credential') {
        alert('You have signed up with a different provider for that email.');
        // Handle linking here if your app allows it.
      } else {
        parseAndThrowError(error);
      }
    }
  }

  private async setTokenAndUser(ctx: StateContext<AuthStateModel>, userResult?: User): Promise<void> {
    ctx.patchState({ loaded: true });
    const token: string = await userResult!.getIdToken();
    this.dispatchSetTokenAndUser(ctx, token, userResult!);
  }

  private dispatchSetTokenAndUser(ctx: StateContext<AuthStateModel>, token: string, user: User) {
    ctx.dispatch(new SetToken(token));
    ctx.dispatch(new SetLoggedInUser(user));

    if (this.isOnboardingWeb) {
      this.router.navigate(['/', 'vendor'], { queryParamsHandling: 'merge' });
    }

    // TODO: rewrite please
    if (this.isAdminWeb) {
      this.router.navigate(['/', 'dashboard'], { queryParamsHandling: 'merge' });
    }
    
    // TODO: rewrite please
    if (this.isAccountWeb) {
      const originUrl = sessionStorage.getItem('originUrl');
      !!originUrl && originUrl !== 'null'
        ? this.router.navigate([ 'auth', 'authenticate' ], { fragment: 'redirecting', queryParamsHandling: 'preserve' })
        : this.router.navigate([ 'user', 'details' ]);
    }

    if (this.isBackOfficeWeb) {
      this.router.navigate(['/']);
      this.snackbarService.destroyAll();
    }
  }

  private applicationSignOut(message?: string) {

    // rewrite please...

    this.snackbarService.destroyAll();

    setTimeout(() => {
      this.router.navigate(['/', 'auth', 'login'], {
        queryParams: { p: null },
        queryParamsHandling: this.isOnboardingWeb ? 'preserve' : 'merge',
        preserveFragment: true
      }).then(() => {
        
        !!!message
          ? this.snackbarService.openSnack({ message: 'Successfully signed out!', duration: 5000 })
          : this.snackbarService.openSnack({ message, duration: Infinity });
      });
    });
  }
}
